import { MultiPolygon, Polygon, LineString, Position } from 'src/common-services'
import { HeatMapDataWeigted } from 'src/components/map/google-map/heatmap'
import { Bounds, Circle, PositionProp } from 'src/types/map'
import { LngLatBounds } from 'mapbox-gl'

export type Project = (lng: number, lat: number) => [number, number]
export type RenderCallback = (project: Project) => React.ReactElement

export const getDomOverlay = (onDraw: () => void) => {
  class DomOverlay extends google.maps.OverlayView {
    public node: HTMLDivElement = document.createElement('div')
    public added: boolean = false
    private readonly onDraw: () => void
    constructor(onDraw: () => void) {
      super()
      this.onDraw = onDraw
    }

    onAdd() {
      this.getPanes()?.overlayMouseTarget.appendChild(this.node)
      this.added = true
    }

    renderCallback: Project = (lng: number, lat: number): [number, number] => {
      const projection = this.getProjection()
      if (!projection) {
        throw new Error('No projection')
      }
      const p = projection.fromLatLngToDivPixel(new google.maps.LatLng(lat, lng))
      if (!p) {
        throw new Error('Could not project point')
      }
      return [p.x, p.y]
    }

    onRemove() {
      this.getPanes()?.overlayMouseTarget.removeChild(this.node)
    }

    draw() {
      this.onDraw()
    }
  }
  return new DomOverlay(onDraw)
}

const isLatLng = (val: PositionProp): val is google.maps.LatLngLiteral => {
  return !Array.isArray(val)
}

export const isPolygon = (shape: MultiPolygon | Polygon | { type: string }): shape is Polygon => {
  return shape.type === 'Polygon'
}

export const isMultiPolygon = (shape: MultiPolygon | Polygon | { type: string }): shape is MultiPolygon => {
  return shape.type === 'MultiPolygon'
}

export const toLatLng = (position: PositionProp): google.maps.LatLngLiteral => {
  if (isLatLng(position)) {
    return position
  }
  return { lat: position[1], lng: position[0] }
}

export const toLatLngBounds = (bounds: Bounds): google.maps.LatLngBounds => {
  const sw = toLatLng(bounds.southWest)
  const ne = toLatLng(bounds.northEast)
  const latLngBounds = new google.maps.LatLngBounds(sw, ne)
  return latLngBounds
}

export const toBounds = (latLngBounds: google.maps.LatLngBounds): Bounds => {
  return {
    center: toPosition(latLngBounds.getCenter()),
    northEast: toPosition(latLngBounds.getNorthEast()),
    southWest: toPosition(latLngBounds.getSouthWest()),
  }
}

const toLatLngArray = (postions: Position[]): google.maps.LatLngLiteral[] => {
  return postions.map((val) => toLatLng(val))
}

export const lineStringToPath = (polygon: LineString): google.maps.LatLngLiteral[] => {
  return toLatLngArray(polygon.coordinates)
}

export const polygonToPaths = (polygon: Polygon): google.maps.LatLngLiteral[][] => {
  return polygon.coordinates.map((x) => toLatLngArray(x))
}

export const multiPolygonToPaths = (polygon: MultiPolygon): google.maps.LatLngLiteral[][][] => {
  const result = polygon.coordinates.map((x) => x.map((y) => toLatLngArray(y)))
  return result
}

/**
 * Converts from a geoJSON shape to google maps style path
 * @param shape The shape to convert to a google maps path
 * @returns A path shape to pass to google maps line/polygon
 */
export const shapeToPaths = (
  shape: Polygon | MultiPolygon | { type: string }
): google.maps.LatLngLiteral[][] | google.maps.LatLngLiteral[][][] => {
  if (isPolygon(shape)) {
    return polygonToPaths(shape)
  }
  if (isMultiPolygon(shape)) {
    return multiPolygonToPaths(shape)
  }
  throw new Error('Unrecognized shape type: ' + shape.type)
}

// TODO(bmc): is this really much better than just allowing `foo!` in eslint?
const notNull = <T>(arg: T | null | undefined, name: string): T => {
  if (arg == null) {
    throw new Error(`"${name}" is null`)
  }
  return arg
}

export const toPosition = (latLng: google.maps.LatLng): Position => {
  return [latLng.lng(), latLng.lat()]
}

const toPoint = (map: google.maps.Map, lng: number, lat: number): [number, number] => {
  const proj = notNull(map.getProjection(), 'projection')
  const pos = new google.maps.LatLng({ lng, lat })
  const point = notNull(proj.fromLatLngToPoint(pos), 'point')
  return [point.x, point.y]
}

/**
 * Converts map bounds to polygon
 * @param bounds Map bounds
 */
export const boundsToPolygon = (bounds: Bounds): Polygon => {
  const { northEast, southWest } = bounds
  const coordinates = [
    [
      [northEast[0], northEast[1]],
      [northEast[0], southWest[1]],
      [southWest[0], southWest[1]],
      [southWest[0], northEast[1]],
      [northEast[0], northEast[1]],
    ],
  ]
  return { type: 'Polygon', coordinates }
}

/**
 * Converts map bounds to a MultiPolygon
 * @param bounds Map bounds
 * @returns a multipolygon representation of the map bounds
 */
export const toMultiPolygon = (bounds: Bounds): MultiPolygon => {
  const { northEast, southWest } = bounds
  const coordinates = [
    [
      [northEast[0], northEast[1]],
      [northEast[0], southWest[1]],
      [southWest[0], southWest[1]],
      [southWest[0], northEast[1]],
      [northEast[0], northEast[1]],
    ],
  ]
  return { type: 'MultiPolygon', coordinates: [coordinates] }
}

/**
 * Convert from LatLng to screen pixel space
 * @param map map instance
 * @param position the coordinate in map LngLat to convert to screen pixel space
 * @returns [x, y] to absolutely position an element on the map in screen pixel space
 */
export const fromPositionToPixel = function (map: google.maps.Map, position: number[]): Position {
  const scale = Math.pow(2, notNull(map.getZoom(), 'zoom'))
  const bounds = notNull(map.getBounds(), 'bounds')
  const nw = toPoint(map, bounds.getSouthWest().lng(), bounds.getNorthEast().lat())
  const point = toPoint(map, position[0], position[1])
  return [Math.floor((point[0] - nw[0]) * scale), Math.floor((point[1] - nw[1]) * scale)]
}

export const toWeightedLocations = (points: HeatMapDataWeigted[]): google.maps.visualization.WeightedLocation[] => {
  return points.map((point) => {
    return {
      location: new google.maps.LatLng(point.location[0], point.location[1]),
      weight: point.weight,
    }
  })
}

/**
 * Get the zoom level to fit the bounds
 * @param bounds Bounds of the
 * @param mapWidth Google Map container width in pixels
 * @param mapHeight Google Map container height in pixels
 * @returns zoom level integer
 */
export const getZoomLevelbyBounds = (bounds: Bounds, mapWidth: number, mapHeight: number): number => {
  const WORLD_DIM = { height: 256, width: 256 }
  const ZOOM_MAX = 21

  const latRad = (lat: number) => {
    const sin = Math.sin((lat * Math.PI) / 180)
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2
  }

  const zoom = (mapPx: number, worldPx: number, fraction: number) => {
    return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.log(2))
  }

  const latFraction = (latRad(bounds.northEast[1]) - latRad(bounds.southWest[1])) / Math.PI
  const lngDiff = bounds.northEast[0] - bounds.southWest[0]
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360
  const latZoom = zoom(mapHeight, WORLD_DIM.height, latFraction)
  const lngZoom = zoom(mapWidth, WORLD_DIM.width, lngFraction)
  return Math.min(latZoom, lngZoom, ZOOM_MAX)
}

/**
 * Converts a google maps circle to a simple, json serializable circle representation
 * @param e a circle in google maps format
 * @returns A simple, json serializable circle
 */
export function convertToCircle(e: google.maps.Circle): Circle {
  const center = e.getCenter()
  if (!center) {
    throw new Error('circle has no center')
  }
  return { radiusMeters: e.getRadius(), center: toPosition(center) }
}

/**
 * Converts a google maps polygon to a geoJSON polygon
 * @param e google maps polygon represenation
 * @returns A geoJSON polygon
 */
export function convertToPolygon(e: google.maps.Polygon): Polygon {
  const path = e.getPath()
  const points = path.getArray().map((x) => toPosition(x))
  const polygon: Polygon = {
    type: 'Polygon',
    coordinates: [points.concat([points[0]])],
  }
  return polygon
}

/**
 * Converts a google maps PolyLine to a geoJSON polygon
 * @param e google maps polyline represenation
 * @returns A geoJSON linestring
 */
export function convertToLineString(e: google.maps.Polyline): LineString {
  const path = e.getPath()
  const points = path.getArray().map((x) => toPosition(x))
  const polygon: LineString = {
    type: 'LineString',
    coordinates: points,
  }
  return polygon
}

export const convertToProjectBounds = (bounds: LngLatBounds): Bounds => ({
  northEast: bounds.getNorthEast().toArray(),
  southWest: bounds.getSouthWest().toArray(),
  center: bounds.getCenter().toArray(),
})

export const isValidPosition = (position: Position | undefined): boolean => {
  if (position === undefined) {
    return false
  }
  return isNaN(position[0]) || isNaN(position[1]) ? false : true
}

export const setPaintProperty = (
  map: mapboxgl.Map,
  layer: string,
  name: string,
  value: any,
  options?: mapboxgl.FilterOptions | undefined
): mapboxgl.Map => {
  if (map.getLayer(layer) === undefined) {
    return map
  }
  return map.setPaintProperty(layer, name, value, options)
}

export const setLayoutProperty = (
  map: mapboxgl.Map,
  layer: string,
  name: string,
  value: any,
  options?: mapboxgl.FilterOptions | undefined
): mapboxgl.Map => {
  if (map.getLayer(layer) === undefined) {
    return map
  }
  return map.setLayoutProperty(layer, name, value, options)
}

export const lastLocationKey = 'last-location'

export const austin = {
  center: [-97.7383476477957, 30.237621629711796],
  northEast: [-97.65234529794219, 30.319486424236427],
  southWest: [-97.82434999764922, 30.155756835187166],
}

export const validateStoredBounds = (bounds: any) => {
  const props: Array<keyof Bounds> = ['center', 'northEast', 'southWest']
  for (const prop of props) {
    if (!bounds[prop] || !bounds[prop][0] || !bounds[prop][1]) {
      return false
    }
  }
  return true
}

export const getLastLocation = () => {
  try {
    const val = localStorage.getItem(lastLocationKey)
    if (val) {
      const obj = JSON.parse(val) ?? austin
      if (validateStoredBounds(obj)) {
        return obj
      }
    }
    return austin
  } catch (e) {
    return austin
  }
}
